<template>
  <q-dialog v-model="shown">
    <q-card style="width: 530px;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          User Photo
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <video
          v-show="!captured"
          ref="monitor"
          class="full-width"
          autoplay
        />
        <canvas
          v-show="captured && saving"
          ref="photo"
          class="full-width"
        />
        <q-img
          v-show="captured && !saving"
          :src="user ? user.photoUrl : null"
          class="full-width rounded-borders"
          @click="$refs.userPhotoDialog.show()"
        />
        <image-uploader
          v-if="currentUser && currentUser.superUser"
          :model-value="user ? user.photoUrl : null"
          :model="user"
          @update:model-value="photoFileKey => user.photo = photoFileKey"
          @loading="uploadImage"
        />
        <q-inner-loading :showing="saving" />
      </q-card-section>
      <q-card-section>
        <q-inner-loading :showing="saving" />
      </q-card-section>
      <q-card-actions class="row justify-between q-ma-sm">
        <q-btn
          v-show="videoDevices.length > 1"
          class="col-auto"
          size="lg"
          icon="cached"
          outline
          @click="videoDeviceIndex += 1"
        />
        <q-btn
          v-show="!captured"
          class="col"
          label="Snap Photo"
          size="lg"
          color="positive"
          @click="snapPhoto"
        />
        <q-btn
          v-show="captured"
          class="col"
          label="Retake"
          color="secondary"
          size="lg"
          outline
          :dense="false"
          @click="captured = false"
        />
        <q-btn
          v-show="captured"
          v-close-popup
          class="col"
          label="Done"
          size="lg"
          color="primary"
          :dense="false"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>

import ImageUploader from 'src/modules/admin/components/ImageUploader'

export default {
  name: 'UserPhotoDialog',
  components: {
    ImageUploader
  },
  props: {
    user: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['close'],
  data () {
    return {
      shown: false,
      captured: false,
      saving: false,
      videoDevices: [],
      videoDeviceIndex: 0
    }
  },
  watch: {
    shown () {
      if (!this.shown) {
        this.$emit('close')
        this.teardownPhoto()
        this.captured = false
      }
    },
    videoDeviceIndex () {
      this.setupVideoStream()
    }
  },
  unmounted () {
    this.teardownPhoto()
  },
  context: ['currentUser'],
  methods: {
    async show () {
      this.shown = true
      this.captured = false
      if (navigator.mediaDevices) {
        // Requires secure context, e.g. HTTPS
        await this.setupDeviceList()
        this.setupDeviceListMonitoring()
        this.setupVideoStream()
      }
    },
    setupDeviceListMonitoring () {
      this.deviceEventListener = (event) => {
        this.setupDeviceList()
      }
      navigator.mediaDevices.addEventListener('devicechange', this.deviceEventListener)
    },
    async setupDeviceList () {
      const devices = await navigator.mediaDevices.enumerateDevices()
      this.videoDevices = _.filter(devices, { kind: 'videoinput' })
    },
    async setupVideoStream () {
      // Need to wait until monitor is mounted on dom
      await this.$nextTick()

      const monitor = this.$refs.monitor
      try {
        const constraints = {
          audio: false,
          video: {
            width: { min: 500, ideal: 1000, max: 1000 },
            height: { min: 500, ideal: 1000, max: 1000 },
            deviceId: this.videoDevices[this.videoDeviceIndex % this.videoDevices.length].deviceId
          }
        }
        monitor.srcObject = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (err) {
        console.error(err)
      }
    },
    async snapPhoto () {
      this.captured = true

      // Need to wait until photo is mounted on dom
      await this.$nextTick()

      const photo = this.$refs.photo

      photo.width = 1000
      photo.height = 1000
      photo.getContext('2d').drawImage(this.$refs.monitor, 0, 0)

      this.save()
    },
    async uploadImage (isLoading) {
      this.saving = isLoading
      this.captured = !isLoading
    },
    async save () {
      this.saving = true
      const photo = this.$refs.photo

      const resizedData = photo.toDataURL('image/jpeg', 0.9)

      const imageFile = this.$directUpload.dataUrlToFile(resizedData, 'user_image.jpeg')

      const { key } = await this.$directUpload.uploadFile(imageFile)

      this.user.photo = key
      await this.user.save()

      this.saving = false
    },
    teardownPhoto () {
      if (navigator?.mediaDevices?.removeEventListener) navigator.mediaDevices.removeEventListener('devicechange', this.deviceEventListener)

      if (!this.$refs.monitor?.srcObject) return

      this.$refs.monitor.srcObject.getTracks().forEach(track => track.stop())
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
